import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { startOfDay } from "~/utils/date.jsx";
import { decimalToMinutes, descSortCompare } from "~/utils/utils.jsx";
import TimeRecordFormList from "./TimeRecordFormList.jsx";
import { Group } from "../ProjectTime/Timelist/Group.js";
import { LimitedAccess } from "../ProjectTime/Timelist/LimitedAccess.js";
import { timeRecordShape } from "../Timesheet/shapes/index.jsx";
import { useTimeRecordModalContext } from "../Tracking/context/TimeRecordModalContext.jsx";

const calculateTimeRecords = (timeRecords, date) => {
  const filteredTimeRecords = timeRecords
    .filter(({ recordDate }) => startOfDay(parseFloat(recordDate)) === startOfDay(parseFloat(date)))
    .sort((a, b) => descSortCompare(a.createdOn, b.createdOn));

  if (filteredTimeRecords.length > 0) {
    const totalValues = {};
    let total = 0;

    filteredTimeRecords.forEach(({ billableStatus, value = 0 }) => {
      if (!totalValues[billableStatus]) {
        totalValues[billableStatus] = 0;
      }
      totalValues[billableStatus] += decimalToMinutes(value);

      total += decimalToMinutes(value);
    });

    return {
      total,
      totalValues,
      filteredTimeRecords,
    };
  }

  return {
    total: null,
    totalValues: {},
    filteredTimeRecords: null,
  };
};

const RecordList = ({ date, onEdit, timeRecords, projectId }) => {
  const { total, totalValues, filteredTimeRecords } = useMemo(
    () => calculateTimeRecords(timeRecords, date),
    [timeRecords, date]
  );

  return filteredTimeRecords ? (
    <Group
      date={date}
      total={total}
      totalValues={totalValues}
      notBillable={totalValues[0]}
      billable={totalValues[1]}
      pendingPayment={totalValues[2]}
      paid={totalValues[3]}
      className="tw-px-1 tw-flex-1 tw-justify-between"
      hideDivider
      projectId={projectId}
    >
      <TimeRecordFormList
        timeRecords={filteredTimeRecords}
        onEdit={onEdit}
      />
    </Group>
  ) : null;
};

RecordList.propTypes = {
  date: PropTypes.number,
  projectId: PropTypes.number.isRequired,
  onMoveToTrash: PropTypes.func,
  onEdit: PropTypes.func,
  timeRecords: PropTypes.array,
};

const TimeRecordListGroupedByDate = ({ timeRecords, limitedAccessCount, onEdit, className, projectId, parentId }) => {
  const { showLimitedAccess = false, paginationDate } = useTimeRecordModalContext();

  const filteredTimeRecords = timeRecords.filter((timeRecord) => {
    if (paginationDate && timeRecord.recordDate <= paginationDate) {
      return false;
    }
    return true;
  });

  const timeRecordDates = useMemo(() => {
    const result = [];

    filteredTimeRecords.forEach(({ recordDate }) => {
      if (!result.includes(startOfDay(parseFloat(recordDate)))) {
        result.push(recordDate);
      }
    });

    return result.sort((a, b) => descSortCompare(a, b));
  }, [filteredTimeRecords]);

  return (
    <div className={className}>
      {timeRecordDates.map((date) => {
        return (
          <RecordList
            key={date}
            date={date}
            onEdit={onEdit}
            timeRecords={filteredTimeRecords}
            projectId={projectId}
          />
        );
      })}
      {showLimitedAccess && limitedAccessCount > 0 ? (
        <LimitedAccess
          projectId={projectId}
          parentId={parentId}
          parentType="Task"
          shouldShowLimitedAccess
        />
      ) : null}
    </div>
  );
};

TimeRecordListGroupedByDate.propTypes = {
  timeRecords: PropTypes.arrayOf(timeRecordShape),
  limitedAccessCount: PropTypes.number,
  projectId: PropTypes.number.isRequired,
  parentId: PropTypes.number.isRequired,
  className: PropTypes.string,
  onMoveToTrash: PropTypes.func,
  onEdit: PropTypes.func,
};

TimeRecordListGroupedByDate.defaultProps = {
  timeRecords: [],
  limitedAccessCount: 0,
};

export default TimeRecordListGroupedByDate;
